import { useEffect, useState, useRef } from "react";
import MessageInput from "./subcomponents/message-input/message-input";
import Message from "./subcomponents/message/message";
import { useGlobalStore } from "~/stores/store-provider";
import { GamgeeChatMessageRead } from "~/types/message";

const ChatWindow = ({sessionId}: {sessionId: string;}) => {
  const {
    chatMessages,
    fetchChatMessages,
    sendMessage,
    clearChatMessages,
    fetchCurrentSession,
    currentSession,
  } = useGlobalStore((state: any) => state);

  const endOfChatRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const isMessageStreaming =
    chatMessages.length > 0 &&
    chatMessages[chatMessages.length - 1].message.streaming;

  const onSendMessage = async (message: string, attachments?: File[]) => {
    setIsLoading(true);
    await sendMessage(sessionId, message, attachments);
    setIsLoading(false);
  };

  const shouldAutoScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      return scrollHeight - scrollTop - clientHeight < 100;
    }
    return false;
  };

  const scrollToBottom = (force: boolean = false, animate: boolean = true) => {
    if (containerRef.current && endOfChatRef.current) {
      if (force || shouldAutoScroll()) {
        endOfChatRef.current.scrollIntoView({
          behavior: animate ? "smooth" : "auto",
        });
      }
    }
  };

  useEffect(() => {
    clearChatMessages();
    fetchChatMessages(sessionId);
    fetchCurrentSession(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (chatMessages && chatMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom(true, false);
      }, 0);
    }
  }, [chatMessages]);

  return (
    <div className="h-full w-full flex flex-col">
      <div ref={containerRef} className="flex-1 overflow-y-auto w-full">
        <div className="flex flex-col max-w-4xl mx-auto pb-8">
          <div>
            {chatMessages.map(
              (message: GamgeeChatMessageRead, index: number) => {
                const isPenultimateMessage = index === chatMessages.length - 2;
                if (isPenultimateMessage) {
                  return (
                    <div key={index} ref={endOfChatRef}>
                      <Message
                        message={message}
                        key={index}
                        scrollToBottom={scrollToBottom}
                      />
                    </div>
                  )
                }
                return (
                  <Message
                    message={message}
                    key={index}
                    scrollToBottom={scrollToBottom}
                  />
                )
              }
            )}
            <div ref={endOfChatRef} />
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 flex justify-center">
        <MessageInput
          onSendMessage={onSendMessage}
          placeholder="Type a message..."
          isMessageStreaming={isMessageStreaming}
          isLoading={isLoading}
          selectedDatabase={currentSession?.database}
          isDatabaseSelectionDisabled={true}
          selectedDataSources={currentSession?.data_source_ids || []}
        />
      </div>
      <div className="flex-shrink-0 flex justify-center pb-4 pl-4 pr-4">
        <span className="text-xs text-neutral-content">Tip: Click Document Mode on the right to create shareable reports</span>
      </div>
    </div>
  );
};

export default ChatWindow;
